<template>
  <v-app>
    <div id="screen-left-anchor" class="fixed-left-anchor"></div>
    <div>
      <v-scroll-x-transition>
        <pre-call-view v-if="app.isPreCall && callTab.state.value === 'active'" />
        <meeting-view v-if="app.isCall" />
        <end-call-view v-if="app.isEnd" />
      </v-scroll-x-transition>
    </div>
    <call-tab-view v-if="callTabNotice" :moved="callTab.state.value === 'moved'" @use-here="useHere" />
  </v-app>
</template>
<style lang="scss">
html,
body {
  overscroll-behavior: none;
  -webkit-overflow-behavior: none;
}
.fixed-left-anchor {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: 9999;
}
</style>
<script setup>
import {computed, watchEffect} from 'vue'
import {useAppStore, useSessionStore, useUserStore} from '@/stores'
import {useStreamStore} from '@/stores/streamStore'
import {useCallTab, useLanguage} from '@/composables'
import {jsonRpc, mediator} from '@communication'
import process from 'process'
import './style.scss'

import {useBackendNotificationCallback} from './procedures/useBackendNotification'
import {useChangeStrategyCallback} from './procedures/useChangeStrategy'
import {useConnectionFailedCallback} from './procedures/useConnectionFailed'
import {useParticipantDisjoinedCallback} from './procedures/useParticipantDisjoined'
import {useParticipantReadyCallback} from './procedures/useParticipantReady'
import {usePeerErrorCallback} from './procedures/usePeerError'
import {useConnectionsCallback} from './procedures/useConnections'
import {useProfileCallback} from './procedures/useProfile'
import {useRecordingCallback} from './procedures/useRecording'
import {useDestroyCallback} from './procedures/useDestroy'
import {useLiveCaptionCallback} from './procedures/useLiveCaption'
import {useParticipantDisconnectedCallback} from './procedures/useParticipantDisconnected'
import {useParticipantJoinedCallback} from './procedures/useParticipantJoined'
import {useRoleExchangeCallback} from './procedures/useRoleExchange'
import {useTransformCallback} from './procedures/useTransform'
import {useSignalCallback} from './procedures/useSignal'
import {useNetworkCallback} from './procedures/useNetwork'
import {useLogCallback} from './procedures/useLog'
import {useVideoHealthCheckCallback} from './procedures/useVideoHealthCheck'
import {useSnapshotCallback} from '@/composables/useSnapshot'
import {useStreamCallback} from './procedures/useStream'
import {useMediaRecoveryCallback} from './procedures/useMediaRecovery'
import {useStartSessionCallback} from './procedures/useStartSession'
import {useDocumentEventCallback} from './procedures/useDocumentEvent'
import {useViewportHeightCallback} from './procedures/useViewportHeight'
import {useTranslation} from 'i18next-vue'

const app = useAppStore()
window.process = process

watchEffect(() => {
  document.body.style.backgroundColor = app.isCall ? '#000' : '#FFF'
})
const {i18next} = useTranslation()
const setupProcedures = () => {
  useBackendNotificationCallback()
  useChangeStrategyCallback()
  useConnectionFailedCallback()
  useParticipantDisjoinedCallback()
  useParticipantReadyCallback()
  usePeerErrorCallback()
  useStreamCallback()
  useMediaRecoveryCallback()
  useStartSessionCallback()
  useDocumentEventCallback()
  useViewportHeightCallback()
  useProfileCallback()
  useRecordingCallback()
  useDestroyCallback()
  useLiveCaptionCallback()
  useParticipantDisconnectedCallback()
  useParticipantJoinedCallback()
  useRoleExchangeCallback()
  useTransformCallback()
  useSignalCallback()
  useNetworkCallback()
  useLogCallback()
  useSnapshotCallback()
  useVideoHealthCheckCallback()
  !!window.localStorage.getItem('airelink:logger') && useConnectionsCallback()
}

const {language, initialize} = useLanguage()
setupProcedures()

// One call page per browser in the client app (useCallTab): another page shows a notice instead of the pre-call
// screen and does not ask for the camera, and "Use here" moves the call to it
const callTab = useCallTab()
const callTabNotice = computed(() => ['elsewhere', 'moved'].includes(callTab.state.value))
// streamStore no longer asks for the permission when it is created: done here once this page has the call
const askDevicePermissions = () => {
  const {camera, microphone} = window.__INITIAL_STATE__.app.idealConstraints
  if (camera || microphone) useStreamStore().ensurePermissions()
}
// Another page took the call over. In the call: leave it as closing the page would (disjoin, camera and microphone
// off) and close the signaling socket, so nothing reconnects or joins again; a reload would ask "Leave site?" here.
// Before the call: reload, which also drops a camera request that is still pending
const leaveForOtherPage = () => {
  if (!app.isCall) return callTab.reloadAsMoved()
  // As useConnectionFailed: no "Leave site?" when this page is closed, no reaction to the others leaving
  useSessionStore().sessionFailed = true
  try {
    mediator.destroy()
    useUserStore().disjoin()
    useStreamStore().stopAll()
  } catch (error) {
    console.error('[callTab] Leaving the call failed', error)
  }
  jsonRpc.close()
}
// The call is over on this page: another page may start one
watchEffect(() => {
  if (app.isEnd) callTab.end()
})
callTab.start(leaveForOtherPage).then(active => active && askDevicePermissions())
const useHere = async () => {
  if (callTab.state.value === 'moved') return callTab.restart()
  if (await callTab.takeOver()) askDevicePermissions()
}

// The English texts are bundled (i18n.js): in English the app starts at once and shows the Locize texts when they
// arrive. Other languages wait for their texts (at most the timeout of useTranslations.js); without them the app
// starts in English.
initialize(language.value).catch(error =>
  console.error('Texts could not be loaded from Locize, using the bundled English ones', error)
)
</script>
